import { CbhIcon, Rating, Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Divider, Stack, TextField } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useFormContext } from "react-hook-form";

import { ReviewQuestionType } from "../../redesign/Workplace/Review/types";
import {
  type ConsolidatedQuestionItem,
  type PostShiftReviewFormValues,
  type StepAction,
} from "../types";
import { NumberValidationResult, validateNumberInput } from "../utils/validateNumberInput";

export interface RateWorkplaceQuestionsStepProps {
  facilityName: string;
  questions: ConsolidatedQuestionItem[];
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function RateWorkplaceQuestionsStep({
  facilityName,
  questions,
  isSubmitting = false,
  onAction,
}: RateWorkplaceQuestionsStepProps) {
  const { watch, setValue, getValues } = useFormContext<PostShiftReviewFormValues>();

  const ratingAnswers = watch("ratingAnswers") ?? {};
  const patientCountAnswer = watch("patientCountAnswer");

  const hasAnyAnswer =
    Object.values(ratingAnswers).some(
      (value) => isDefined(value) && value !== "" && Number(value) > 0
    ) ||
    (isDefined(patientCountAnswer) && patientCountAnswer !== "");

  function handleRatingChange(questionId: string, newValue: number | null) {
    if (newValue === null) {
      return;
    }

    const currentRatingAnswers = getValues("ratingAnswers") ?? {};
    setValue(
      "ratingAnswers",
      { ...currentRatingAnswers, [questionId]: String(newValue) },
      { shouldValidate: true }
    );
  }

  function handleNumberChange(value: string) {
    const numericValue = value.replace(/[^0-9]/g, "");
    setValue("patientCountAnswer", numericValue, { shouldValidate: true });
  }

  function getNumberValidation(question: ConsolidatedQuestionItem): NumberValidationResult {
    if (!isDefined(question.numberConfig)) {
      return { isError: false };
    }

    return validateNumberInput({
      value: patientCountAnswer,
      minValue: question.numberConfig.minValue,
      maxValue: question.numberConfig.maxValue,
    });
  }

  const hasNumberValidationError = questions.some((question) => {
    if (question.questionType !== ReviewQuestionType.NUMBER) {
      return false;
    }

    const validation = getNumberValidation(question);
    return validation.isError;
  });

  return (
    <>
      <DialogContent sx={{ overflowY: "auto" }}>
        <Stack spacing={3}>
          <Text
            variant="caption"
            sx={(theme) => ({
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: theme.palette.text.secondary,
            })}
          >
            {facilityName}
          </Text>

          <Title variant="h4" component="h2">
            Rate this workplace
          </Title>

          <Text variant="body2" color="text.secondary">
            Answer as many as you like.
          </Text>

          <Stack spacing={0} sx={{ paddingTop: 4 }}>
            {questions.map((question, index) => {
              const numberValidation =
                question.questionType === ReviewQuestionType.NUMBER
                  ? getNumberValidation(question)
                  : undefined;

              return (
                <Box key={question.questionId}>
                  {index > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" spacing={3} sx={{ paddingY: 4 }}>
                    <Box
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.background.secondary,
                        flexShrink: 0,
                      })}
                    >
                      <CbhIcon type={question.iconType} size="small" />
                    </Box>

                    <Text variant="body1" sx={{ flex: 1 }}>
                      {question.questionText}
                    </Text>

                    {question.questionType === ReviewQuestionType.RATING && (
                      <Rating
                        value={Number(ratingAnswers[question.questionId] ?? 0)}
                        precision={1}
                        size="medium"
                        onChange={(_event, newValue) => {
                          handleRatingChange(question.questionId, newValue);
                        }}
                      />
                    )}

                    {isDefined(numberValidation) && (
                      <TextField
                        size="small"
                        placeholder="e.g. 8"
                        value={patientCountAnswer ?? ""}
                        type="text"
                        error={numberValidation.isError}
                        helperText={
                          numberValidation.isError ? numberValidation.errorMessage : undefined
                        }
                        inputProps={{
                          pattern: "[0-9]*",
                          inputMode: "numeric",
                        }}
                        sx={{ width: 100, flexShrink: 0 }}
                        onChange={(event) => {
                          handleNumberChange(event.target.value);
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%", height: "100px" }}>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isSubmitting}
            disabled={!hasAnyAnswer || hasNumberValidationError}
            onClick={() => {
              onAction({ type: "NEXT_STEP" });
            }}
          >
            Continue
          </LoadingButton>
          {!isSubmitting && (
            <Box sx={{ alignSelf: "center" }}>
              <TextButton
                onClick={() => {
                  onAction({ type: "SKIP_STEP" });
                }}
              >
                Skip
              </TextButton>
            </Box>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}
